import React from 'react';
import { css } from '@emotion/core';
import chunk from 'lodash/chunk';
import isEqual from 'lodash/isEqual';
import {
  FormRow,
  PasswordField,
  PrimaryButton,
  SpinningLoader,
  Text,
  TextField,
  useForm,
} from '@weave/design-system';
import { WeaveTheme } from '@weave/theme-original';
import { IntegrationFieldsModel } from '../../../redux/actions/sync-app-settings';
import { formRowSingleChildMargin } from './create-cloud-sync-app.component';
import {
  OfficeMultiSelect,
  OfficeOption,
} from '../../generic/office-multi-select/office-multi-select.component';

type Props = {
  fields: any;
  fieldsMetadata: IntegrationFieldsModel[];
  formComplete: boolean;
  onClick: (values: { [key: string]: any }) => void;
  offices: OfficeOption[];
  selectedOfficeIds: string[];
  onOfficeSelectionChange: (selectedIds: string[]) => void;
  onFetchOffices: (values: Record<string, string>) => void;
  isFetchingOffices: boolean;
  hasFetchedOffices: boolean;
  lastFetchedValues: Record<string, string> | null;
  locationSelectionSupported: boolean;
};

export const SettingsFields = ({
  fields,
  fieldsMetadata,
  onClick,
  formComplete,
  offices,
  selectedOfficeIds,
  onOfficeSelectionChange,
  onFetchOffices,
  isFetchingOffices,
  hasFetchedOffices,
  lastFetchedValues,
  locationSelectionSupported,
}: Props) => {
  const { getFieldProps, isComplete, values } = useForm({ fields });

  const isFormFieldsIncomplete =
    !formComplete || (!isComplete && Object.keys(fields).length > 0);
  const formMatchesLastFetch = !!lastFetchedValues && isEqual(values, lastFetchedValues);
  const hasFetchedOfficesForCurrentValues = hasFetchedOffices && formMatchesLastFetch;
  const showOfficeSelector =
    hasFetchedOfficesForCurrentValues &&
    locationSelectionSupported &&
    !isFormFieldsIncomplete;
  const hasSelectedOffices = selectedOfficeIds.length > 0;
  const isCreateDisabled =
    isFormFieldsIncomplete ||
    isFetchingOffices ||
    !hasFetchedOfficesForCurrentValues ||
    (locationSelectionSupported && !hasSelectedOffices);

  const handleFetchOfficesClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    onFetchOffices(values as Record<string, string>);
  };

  return (
    <>
      {chunk(fieldsMetadata, 2).map((rowFields, rowIndex) => (
        <FormRow
          cols={Array(rowFields.length).fill(50)}
          key={rowIndex}
          css={css`
            width: 800px;
          `}
        >
          {rowFields.map((field, fieldIndex) => {
            const Component = field.valueType !== 'PASSWORD' ? TextField : PasswordField;
            return (
              <Component
                {...getFieldProps(field.name)}
                label={field.displayName}
                key={`${rowIndex}-${fieldIndex}`}
                autoComplete="new-password"
                css={rowFields.length === 1 && formRowSingleChildMargin}
              />
            );
          })}
        </FormRow>
      ))}

      {isFetchingOffices && (
        <div css={loadingRowStyle}>
          <SpinningLoader size="small" />
          <Text as="span" size="medium" color="light">
            Fetching offices…
          </Text>
        </div>
      )}

      {showOfficeSelector && !isFetchingOffices && (
        <div
          css={css`
            width: 800px;
            margin-bottom: 24px;
          `}
        >
          <OfficeMultiSelect
            offices={offices}
            selectedIds={selectedOfficeIds}
            onChange={onOfficeSelectionChange}
          />
        </div>
      )}

      {!hasFetchedOfficesForCurrentValues && (
        <PrimaryButton
          css={css`
            width: 200px;
            margin-bottom: 24px;
          `}
          disabled={isFormFieldsIncomplete || isFetchingOffices}
          onClick={handleFetchOfficesClick}
        >
          Fetch Offices
        </PrimaryButton>
      )}

      <PrimaryButton
        css={css`
          width: 160px;
        `}
        disabled={isCreateDisabled}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          event.preventDefault();
          onClick(values);
        }}
      >
        Create
      </PrimaryButton>
    </>
  );
};

const loadingRowStyle = (theme: WeaveTheme) => css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
  margin-bottom: ${theme.spacing(3)};
`;
